import React, { useEffect, useState } from 'react';
import { useUserData } from '../contexts/UserDataContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

// Define the type for a favorite sentence
interface FavoriteSentence {
    _id?: string;
    en?: string;
    sentence?: string;
    translation?: string;
    category?: string;
    wordCount?: number;
    [key: string]: any;
}

// Change wordCountOptions to [1,2,3,4,5,'6+']
const wordCountOptions = [1, 2, 3, 4, 5, '6+'] as const;
type WordCountType = typeof wordCountOptions[number];

export default function FavoriteSentencesCard() {
    const { getFavoriteSentences, removeFavoriteSentence, userData } = useUserData();
    const { currentUser } = useAuth();
    const [selectedWordCount, setSelectedWordCount] = useState<WordCountType>(1);
    const [sentences, setSentences] = useState<FavoriteSentence[]>([]);
    const [loading, setLoading] = useState(false);
    const [userCount, setUserCount] = useState<number>(0);
    const PAGE_SIZE = 5;
    const [page, setPage] = useState(0);

    // Debug logs at the top
    console.log('FavoriteSentencesCard - userData:', userData);
    console.log('FavoriteSentencesCard - selectedLanguage:', userData?.selectedLanguage);
    console.log('FavoriteSentencesCard - userCount:', userCount);
    console.log('FavoriteSentencesCard - sentences:', sentences);

    useEffect(() => {
        setSentences([]); // Clear old data immediately
        setLoading(true);
        const apiWordCount: number = selectedWordCount === '6+' ? 6 : selectedWordCount as number;
        getFavoriteSentences(apiWordCount)
            .then(res => {
                setSentences(res || []);
                setPage(0);
                // Debug log after API call
                console.log('FavoriteSentencesCard - API sentences:', res);
            })
            .finally(() => setLoading(false));

        // Remove the unnecessary count API call
        // if (currentUser?.uid) {
        //     api.getFavoriteSentencesCount(currentUser.uid, apiWordCount)
        //         .then(count => {
        //             setUserCount(count);
        //             // Debug log after count API call
        //             console.log('FavoriteSentencesCard - API userCount:', count);
        //         })
        //         .catch(err => {
        //             console.error('Error fetching favorite sentences count:', err);
        //             setUserCount(0);
        //         });
        // }
    }, [selectedWordCount, getFavoriteSentences, currentUser?.uid]);

    const maxPage = Math.max(0, Math.ceil(sentences.length / PAGE_SIZE) - 1);
    const pageSentences = sentences.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    return (
        <div className="bg-white/70 backdrop-blur-lg border border-yellow-200 rounded-2xl p-8 shadow-xl max-h-[80vh] overflow-y-auto">
            <div className="max-w-3xl mx-auto">
                <h1 className="text-4xl font-extrabold tracking-tight drop-shadow-lg mb-6 text-center">
                    <span className="bg-gradient-to-r from-yellow-500 via-pink-500 to-yellow-400 bg-clip-text text-transparent">Favorite Sentences</span>
                </h1>
            </div>
            <div className="flex flex-wrap gap-2 justify-center mb-6">
                {wordCountOptions.map(wc => (
                    <button
                        key={wc}
                        className={`px-4 py-2 rounded-full font-medium transition-colors ${selectedWordCount === wc ? 'bg-yellow-600 text-white' : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'}`}
                        onClick={() => setSelectedWordCount(wc as WordCountType)}
                    >
                        {wc === '6+' ? '6+ words' : `${wc} word${wc > 1 ? 's' : ''}`}
                    </button>
                ))}
            </div>
            {loading ? (
                <div className="text-center text-lg text-yellow-500 font-semibold py-12">Loading...</div>
            ) : sentences.length === 0 ? (
                <div className="bg-white/70 backdrop-blur-lg border border-yellow-200 rounded-2xl p-8 shadow-xl">
                    <div className="text-center">
                        <div className="text-4xl mb-4">⭐</div>
                        <p className="text-gray-600">No favorite sentences yet. Start learning to see them here!</p>
                    </div>
                </div>
            ) : (
                <>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
                        {/* Removed Selected Language text */}
                        <div className="text-lg font-bold text-yellow-700">Total: {sentences.length}</div>
                    </div>
                    <div className="flex flex-col gap-4">
                        {pageSentences.map((s, idx) => (
                            <div key={s._id || s.sentence || idx} className="bg-white/70 rounded-2xl p-4 shadow-md border border-yellow-100 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                                <div>
                                    {/* Show selected language on top, English below if not English */}
                                    <div className="text-xl font-bold text-gray-900 mb-1">
                                        {s[userData?.selectedLanguage || 'en'] || s.en || <span className='italic text-gray-400'>Sentence not found</span>}
                                    </div>
                                    {userData?.selectedLanguage !== 'en' && s.en && (
                                        <div className="text-base text-gray-500">{s.en}</div>
                                    )}
                                    {s.category && (
                                        <div className="text-base text-gray-500 italic">{s.category}</div>
                                    )}
                                </div>
                                <button
                                    className="flex items-center gap-2 px-6 py-2 rounded-full bg-gradient-to-r from-red-400 to-red-600 text-white font-bold shadow-lg hover:scale-105 hover:shadow-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-300"
                                    onClick={async () => {
                                        await removeFavoriteSentence(s.itemId);
                                        setSentences(prev => prev.filter((item, i) => i !== idx));
                                    }}
                                >
                                    <span>🗑️</span> Unfavorite
                                </button>
                            </div>
                        ))}
                    </div>
                    {/* Pagination controls */}
                    {sentences.length > PAGE_SIZE && (
                        <div className="flex justify-center gap-4 mt-4">
                            <button
                                className="px-4 py-2 rounded-full bg-gray-200 text-gray-700 font-semibold disabled:opacity-50"
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                                disabled={page === 0}
                            >
                                Previous
                            </button>
                            <span className="px-4 py-2 text-gray-700 font-semibold">Page {page + 1} of {maxPage + 1}</span>
                            <button
                                className="px-4 py-2 rounded-full bg-blue-500 text-white font-semibold disabled:opacity-50"
                                onClick={() => setPage(p => (p + 1 < maxPage + 1 ? p + 1 : p))}
                                disabled={page === maxPage}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
} 